<script>
import Treeselect from '@riophae/vue-treeselect'

// Drop-in replacement for a native <select>, rendered as a searchable vue-treeselect.
// Usage is the same as <select>: <option> children (static or v-for), v-model or :value,
// @change, disabled, multiple, required. Option values keep their original types, like
// Vue's v-model on a native select. @change receives a minimal event whose target.value is
// the string the native element would have reported, so `$event.target.value` handlers work.
//
// An option with an empty value ('' / null / undefined) becomes the placeholder; if it isn't
// disabled the control is clearable, and clearing sets the model back to that empty value.

// Same comparison Vue uses to match a select's v-model against option values.
function looseEqual (a, b) {
  if (a === b) return true
  const aIsObject = a !== null && typeof a === 'object'
  const bIsObject = b !== null && typeof b === 'object'
  if (aIsObject && bIsObject) {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch (e) {
      return false
    }
  }
  if (!aIsObject && !bIsObject) return String(a) === String(b)
  return false
}

function isEmptyValue (value) {
  return value === '' || value === null || value === undefined
}

function domString (value) {
  return value === null || value === undefined ? '' : String(value)
}

function vnodeText (vnodes) {
  return (vnodes || []).map(vnode => {
    if (vnode.text !== undefined && !vnode.tag) return vnode.text
    return vnodeText(vnode.children)
  }).join('')
}

// Reads <option> vnodes from the slot. `:value` compiles to domProps, static value="" to attrs,
// and an option without a value falls back to its text, as in the DOM.
function readOptions (vnodes) {
  const options = []
  ;(vnodes || []).forEach(vnode => {
    if (vnode.tag === 'option') {
      const data = vnode.data || {}
      const attrs = data.attrs || {}
      const domProps = data.domProps || {}
      const label = vnodeText(vnode.children).replace(/\s+/g, ' ').trim()
      let value = label
      if ('value' in domProps) value = domProps.value
      else if ('value' in attrs) value = attrs.value
      options.push({
        value,
        label,
        disabled: attrs.disabled !== undefined && attrs.disabled !== false,
        selected: Boolean(domProps.selected) || (attrs.selected !== undefined && attrs.selected !== false)
      })
    } else if (vnode.children) {
      options.push(...readOptions(vnode.children))
    }
  })
  return options
}

export default {
  name: 'nv-select',
  props: {
    value: {},
    multiple: Boolean,
    disabled: Boolean,
    required: Boolean,
    name: String,
    placeholder: String
  },
  data () {
    return {
      // Only used when the parent passes neither v-model nor :value
      localValue: undefined
    }
  },
  methods: {
    isControlled () {
      const options = this.$vnode && this.$vnode.componentOptions
      return Boolean(options && options.propsData && 'value' in options.propsData)
    },
    currentValue (options) {
      if (this.isControlled()) return this.value
      // Uncontrolled: follow the options' `selected` flags until the user picks something
      const selected = this.multiple
        ? options.filter(o => o.selected).map(o => o.value)
        : (options.find(o => o.selected) || options[0] || {}).value
      const signature = JSON.stringify(selected === undefined ? null : selected)
      if (signature !== this.$_nvSelectedSignature) {
        this.$_nvSelectedSignature = signature
        this.localValue = undefined
      }
      return this.localValue !== undefined ? this.localValue : selected
    },
    onInput (ids, items, emptyValue, currentIds) {
      if (JSON.stringify(ids === undefined ? null : ids) === JSON.stringify(currentIds)) return
      const byId = id => items.find(item => item.id === id)
      let value
      if (this.multiple) {
        value = (ids || []).map(id => byId(id)).filter(Boolean).map(item => item.raw)
      } else {
        const item = ids === null || ids === undefined ? null : byId(ids)
        value = item ? item.raw : emptyValue
      }
      if (!this.isControlled()) this.localValue = value
      const domValue = this.multiple ? domString(value[0]) : domString(value)
      const target = { value: domValue, name: this.name, multiple: this.multiple }
      this.$emit('input', value)
      this.$emit('change', {
        type: 'change',
        target,
        currentTarget: target,
        preventDefault () {},
        stopPropagation () {}
      })
    }
  },
  render (h) {
    const options = readOptions(this.$slots.default)
    let placeholder = this.placeholder
    let emptyValue = null
    let clearable = false
    let foundEmpty = false
    const items = []
    let longest = 0

    options.forEach((option, index) => {
      longest = Math.max(longest, option.label.length)
      if (!this.multiple && isEmptyValue(option.value) && !foundEmpty) {
        foundEmpty = true
        placeholder = placeholder || option.label || undefined
        emptyValue = option.value
        clearable = !option.disabled
        return
      }
      items.push({ id: index, label: option.label || ' ', isDisabled: option.disabled, raw: option.value })
    })
    // The slot is re-read on every parent render; hand Treeselect the same array when nothing
    // changed so it doesn't rebuild its option tree on each keystroke elsewhere in the form.
    let signature
    try {
      signature = JSON.stringify(items)
    } catch (e) {
      signature = null // circular option values: skip the cache
    }
    if (!this.$_nvItems || signature === null || this.$_nvItems.signature !== signature) {
      this.$_nvItems = { signature, items }
    }
    const stableItems = this.$_nvItems.items

    const current = this.currentValue(options)
    let selectedIds
    if (this.multiple) {
      selectedIds = (Array.isArray(current) ? current : [])
        .map(v => items.find(item => looseEqual(item.raw, v)))
        .filter(Boolean)
        .map(item => item.id)
    } else {
      const match = isEmptyValue(current) ? null : items.find(item => looseEqual(item.raw, current))
      selectedIds = match ? match.id : null
    }
    const hasValue = this.multiple ? selectedIds.length > 0 : selectedIds !== null

    return h('div', {
      class: ['nv-select', { 'nv-select--disabled': this.disabled }],
      style: { '--nv-select-ch': Math.min(Math.max(longest, 8), 40) + 'ch' }
    }, [
      h(Treeselect, {
        props: {
          options: stableItems,
          value: selectedIds,
          multiple: this.multiple,
          disabled: this.disabled,
          clearable,
          searchable: true,
          placeholder: placeholder || 'Select…',
          noResultsText: 'No matches',
          noOptionsText: 'No options',
          appendToBody: true,
          zIndex: 2000
        },
        on: {
          input: ids => this.onInput(ids, items, emptyValue, selectedIds)
        }
      }),
      // Keeps native `required` form validation and `name` working
      h('input', {
        class: 'nv-select-native',
        attrs: { tabindex: -1, 'aria-hidden': 'true', required: this.required, name: this.name },
        domProps: { value: hasValue ? 'selected' : '' }
      })
    ])
  }
}
</script>



// WEBPACK FOOTER //
// NvSelect.vue?0bfd312d